import type { ClawAccess } from '@/app/hooks/use-claw-access';

export interface ComputeCanUploadArgs {
  /** access 三态 + null（hydrate 中） */
  access: ClawAccess | null;
  /** 来自外层 prop，目前用作 access===null 兜底 */
  disableUpload: boolean;
  /** 当前活跃 session id；null / undefined 都代表 welcome 态（无 session，走 buffer 路径） */
  activeSessionId: string | null | undefined;
  /** 当前活跃 claw 实例 id；entitled / 切 claw 中可能为空 */
  activeClawId: string | null | undefined;
  /** 文件正在上传中 */
  uploadingFile: boolean;
}

/**
 * 决策 ChatInput + 上传按钮是否可用。
 *
 * 设计意图：
 * - access===null（hydrate）：兜底禁用，避免文件落到无人接的状态
 * - 欢迎页（无 session）：只要 access 不为 null 即可上传，默认 buffer 到本地
 * - expired + 有 session：buffer 到本地草稿，发送时触发重新开通再上传
 * - ready + 有 session：必须有 activeClawId 才允许上传
 * - entitled 不会出现在有 session 的页面，兜底禁用
 * - uploadingFile=true：禁用避免重复触发
 *
 * 与 BaseClawInputView 内部 isBufferMode / shouldBufferUpload 路径完全对齐。
 */
export const computeCanUpload = ({
  access,
  disableUpload,
  activeSessionId,
  activeClawId,
  uploadingFile,
}: ComputeCanUploadArgs): boolean => {
  if (disableUpload) return false;
  if (uploadingFile) return false;
  if (access === null) return false;

  // 欢迎页（无 session）：只要 access 不为 null，都可以上传到本地，不需要 activeClaw.id
  if (!activeSessionId) return true;

  // expired 态：buffer 到本地草稿，发送时触发重新开通再上传
  if (access === 'expired') return true;

  // ready + 有 session：必须有 activeClawId 才允许上传
  if (access === 'ready') return !!activeClawId;

  // entitled 不会出现在有 session 的页面，兜底禁用
  return false;
};
